import LandingOrb from './LandingOrb'
import type { LandingPersona, LandingPersonaId } from '@/hooks/useLandingPersona'

interface Props {
  persona: LandingPersona
  personaId: LandingPersonaId
  setPersonaId: (id: LandingPersonaId) => void
  PERSONAS: Record<LandingPersonaId, LandingPersona>
}

export default function LandingPersonaShowcase({
  persona,
  personaId,
  setPersonaId,
  PERSONAS,
}: Props) {
  const personas = Object.values(PERSONAS)

  return (
    <section className="lp-section" id="personas">
      <div className="lp-container">
        <div className="lp-section-header">
          <div className="lp-eyebrow">Five voices. One assistant.</div>
          <h2>
            Same data. <span style={{ color: persona.primary }}>Your tone.</span>
          </h2>
          <p className="lp-subtitle">
            Every plan includes all five personas. Each has its own ElevenLabs voice and
            personality. Swap any time from your dashboard.
          </p>
        </div>

        <div className="lp-personas-grid">
          {personas.map((p) => {
            const active = personaId === p.id
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setPersonaId(p.id)}
                className="lp-card lp-persona-card"
                data-active={active}
                style={{
                  borderColor: active
                    ? p.primary
                    : 'rgba(255,255,255,0.07)',
                  boxShadow: active ? `0 0 40px ${p.glow}` : undefined,
                  background: active
                    ? `linear-gradient(180deg, ${p.bgTint} 0%, rgba(255,255,255,0.015) 100%)`
                    : undefined,
                  textAlign: 'left',
                  cursor: 'pointer',
                  color: '#fff',
                  fontFamily: 'inherit',
                }}
              >
                <div className="mini-orb">
                  <LandingOrb
                    personaId={p.id}
                    primary={p.primary}
                    secondary={p.secondary}
                    glow={p.glow}
                    shape={p.orbShape}
                    radiusScale={p.orbRadiusScale}
                    rings={p.rings}
                    nodeCount={Math.round(p.nodeCount * 0.45)}
                    size={170}
                    intensity={active ? 1 : 0.7}
                    interaction={p.interaction}
                    interactive
                  />
                </div>

                <h3 className="name" style={{ color: p.primary }}>
                  {p.name}
                </h3>
                <p className="tag">{p.tagline}</p>

                <blockquote className="quote" style={{ color: 'rgba(255,255,255,0.72)' }}>
                  {p.liveSample}
                </blockquote>

                {active && (
                  <span
                    className="active-pill"
                    style={{
                      background: `color-mix(in srgb, ${p.primary} 18%, transparent)`,
                      color: p.primary,
                      border: `1px solid color-mix(in srgb, ${p.primary} 40%, transparent)`,
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        width: 6,
                        height: 6,
                        borderRadius: 99,
                        background: p.primary,
                        boxShadow: `0 0 10px ${p.primary}`,
                      }}
                    />
                    Active
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </div>
    </section>
  )
}
